import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Minus, Plus, X } from 'lucide-react';
import { useCart } from '../../hooks/useCart';
import { formatPrice } from '../../utils/formatPrice';
import { Button } from '../ui/Button';

const Root = styled.div`
  position: fixed;
  inset: 0;
  z-index: 45;
  pointer-events: ${(p) => (p.$open ? 'auto' : 'none')};
`;

const Backdrop = styled.div`
  position: absolute;
  inset: 0;
  background: hsla(0, 0%, 0%, 0.4);
  opacity: ${(p) => (p.$open ? 1 : 0)};
  transition: opacity 0.3s ease;
`;

const Panel = styled.aside`
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  width: 100%;
  max-width: 28rem;
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.foreground};
  display: flex;
  flex-direction: column;
  transform: translateX(${(p) => (p.$open ? '0' : '100%')});
  transition: transform 0.4s ${({ theme }) => theme.easeLuxe};
  box-shadow: ${({ theme }) => theme.shadows.luxe};
`;

const Head = styled.div`
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const Title = styled.p`
  margin: 0;
  font-size: 11px;
  font-family: ${({ theme }) => theme.fonts.sans};
  letter-spacing: 0.25em;
  text-transform: uppercase;
  font-weight: 500;
`;

const Scroll = styled.div`
  flex: 1;
  overflow-y: auto;
  border-bottom: 1px solid transparent;
`;

const Line = styled.div`
  display: flex;
  gap: 1rem;
  padding: 1.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const Thumb = styled.img`
  width: 96px;
  height: 128px;
  object-fit: cover;
  background: ${({ theme }) => theme.colors.secondary};
`;

const QtyWrap = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

const QtyBtn = styled.button`
  padding: 0.375rem 0.5rem;
  border: none;
  background: transparent;
  &:hover {
    background: ${({ theme }) => theme.colors.secondary};
  }
`;

const Foot = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  padding: 1.5rem;
  background: hsla(38, 25%, 90%, 0.4);
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export function CartDrawer() {
  const { t } = useTranslation();
  const { items, isOpen, close, remove, setQty, subtotal } = useCart();

  return (
    <Root $open={isOpen}>
      <Backdrop $open={isOpen} onClick={close} aria-hidden />
      <Panel $open={isOpen} role="dialog" aria-modal="true" aria-labelledby="cart-title">
        <Head>
          <Title id="cart-title">{t('modamai.cartDrawer.title', { count: items.length })}</Title>
        </Head>

        {items.length === 0 ? (
          <div
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              padding: '2rem',
            }}
          >
            <p
              style={{
                margin: 0,
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: '1.875rem',
              }}
            >
              {t('modamai.cartDrawer.emptyTitle')}
            </p>
            <p style={{ margin: '0.75rem 0 2rem', fontSize: '0.875rem', opacity: 0.7 }}>
              {t('modamai.cartDrawer.emptyLead')}
            </p>
            <Button as={Link} to="/shop/new" onClick={close} $variant="luxe">
              {t('modamai.cartDrawer.shopNewIn')}
            </Button>
          </div>
        ) : (
          <>
            <Scroll>
              {items.map((it, idx) => (
                <Line key={`${it.product.id}-${it.size}-${it.color}-${idx}`}>
                  <Link to={`/product/${it.product.slug}`} onClick={close}>
                    <Thumb src={it.product.image} alt={it.product.name} />
                  </Link>
                  <div
                    style={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: '0.5rem',
                      }}
                    >
                      <Link
                        to={`/product/${it.product.slug}`}
                        onClick={close}
                        style={{
                          fontSize: '0.875rem',
                          fontWeight: 500,
                        }}
                      >
                        {it.product.name}
                      </Link>
                      <button
                        type="button"
                        aria-label={t('modamai.cartDrawer.remove')}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          padding: 0,
                          opacity: 0.6,
                          cursor: 'pointer',
                        }}
                        onClick={() => remove(idx)}
                      >
                        <X size={16} />
                      </button>
                    </div>
                    <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', opacity: 0.65 }}>
                      {t('modamai.cartDrawer.variantLine', { color: it.color, size: it.size })}
                    </p>
                    <div
                      style={{
                        marginTop: 'auto',
                        paddingTop: '0.75rem',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-end',
                      }}
                    >
                      <QtyWrap>
                        <QtyBtn
                          type="button"
                          aria-label={t('modamai.cartDrawer.decreaseQty')}
                          onClick={() => setQty(idx, it.qty - 1)}
                        >
                          <Minus size={12} />
                        </QtyBtn>
                        <span style={{ padding: '0 0.75rem', fontSize: '0.875rem' }}>
                          {it.qty}
                        </span>
                        <QtyBtn
                          type="button"
                          aria-label={t('modamai.cartDrawer.increaseQty')}
                          onClick={() => setQty(idx, it.qty + 1)}
                        >
                          <Plus size={12} />
                        </QtyBtn>
                      </QtyWrap>
                      <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>
                        {formatPrice(it.product.price * it.qty)}
                      </span>
                    </div>
                  </div>
                </Line>
              ))}
            </Scroll>
            <Foot>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: '0.875rem',
                }}
              >
                <span style={{ opacity: 0.65 }}>{t('modamai.cartDrawer.subtotal')}</span>
                <span style={{ fontWeight: 500 }}>{formatPrice(subtotal)}</span>
              </div>
              <p style={{ margin: 0, fontSize: '0.75rem', opacity: 0.65 }}>
                {t('modamai.cartDrawer.shippingTaxNote')}
              </p>
              <Button as={Link} to="/checkout" onClick={close} $variant="luxe" $size="lg" style={{ width: '100%' }}>
                {t('modamai.cartDrawer.checkout')} — {formatPrice(subtotal)}
              </Button>
              <button
                type="button"
                style={{
                  width: '100%',
                  background: 'none',
                  border: 'none',
                  fontSize: '11px',
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                  opacity: 0.65,
                  cursor: 'pointer',
                }}
                onClick={close}
              >
                {t('modamai.cartDrawer.continueShopping')}
              </button>
            </Foot>
          </>
        )}
      </Panel>
    </Root>
  );
}
